import { t } from '@lingui/macro'
import styled, { useTheme } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { DoctorInfo } from '../../components/doctor-info/DoctorInfo'
import { formatFieldOfSpecialisations } from '../../helpers/doctor'

import type { Doctor } from '../types/booking.type'

// Types
export type Caregiver = {
  doctor: Doctor
  specialisation: { display: string }
}

type RecommendedCaregiverSelectProps = {
  caregivers: Caregiver[]
  selectedCaregiverId: number | null | undefined
  onSelect: (id: number | null) => void
}

export const RecommendedCaregiverSelect = ({
  caregivers,
  selectedCaregiverId,
  onSelect,
}: RecommendedCaregiverSelectProps) => {
  const theme = useTheme()
  return (
    <FlexColumn gap={theme.UI.SpacingPx.Space6}>
      <FlexColumn gap={theme.UI.SpacingPx.Space4}>
        <H3 noMargin>Wil je een nieuwe afspraak maken?</H3>
        <StCard
          selected={selectedCaregiverId === -1}
          onClick={() => onSelect(-1)}
          data-testid='recommended-caregiver-other'
        >
          <Span type='BodyMediumRegular'>
            {t({
              id: 'appointments.book.recommended-caregivers.other-doctor',
              message: 'Maak een afspraak bij een zorgverlener',
            })}
          </Span>
        </StCard>
      </FlexColumn>

      <FlexColumn gap={theme.UI.SpacingPx.Space4}>
        <Span type='BodyLargeRegular'>
          {t({
            id: 'appointments.book.recommended-caregivers.description',
            comment: 'Recommended caregivers description',
            message: 'Of wil je opnieuw een afspraak maken bij één van je recente zorgverleners?',
          })}
        </Span>
        {caregivers
          .filter((caregiver) => !!caregiver.doctor)
          .map((caregiver) => {
            const isSelected = selectedCaregiverId === Number(caregiver.doctor?.agendaId)
            const agendaId = caregiver.doctor?.agendaId ?? 'unknown'
            const description = caregiver.specialisation.display || caregiver.doctor.specialisms?.[0]?.title?.nl || ''
            const fields = formatFieldOfSpecialisations(caregiver.doctor.fieldOfSpecialisations)
            return (
              <StCard
                key={agendaId}
                selected={isSelected}
                onClick={() => onSelect(Number(caregiver.doctor?.agendaId))}
                data-testid={`recommended-caregiver-${agendaId}`}
              >
                <DoctorInfo>
                  <DoctorInfo.Avatar images={caregiver.doctor.images} name={caregiver.doctor.name} size='large' />
                  <DoctorInfo.Details>
                    <DoctorInfo.Name type='BodyLargeBold'>{caregiver.doctor.name}</DoctorInfo.Name>
                    {description ? <DoctorInfo.Description>{description}</DoctorInfo.Description> : null}
                    {fields ? <DoctorInfo.Note>{fields}</DoctorInfo.Note> : null}
                    {caregiver.doctor.conventionStatus?.nl ? (
                      <DoctorInfo.Note>{caregiver.doctor.conventionStatus.nl}</DoctorInfo.Note>
                    ) : null}
                  </DoctorInfo.Details>
                </DoctorInfo>
              </StCard>
            )
          })}
      </FlexColumn>
    </FlexColumn>
  )
}

const StCard = styled(Card).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'selected',
})<{ selected: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`};
  border: 1px solid ${({ theme }) => theme.Components.Conversational.Border};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;

  transition-property: border, background, color;
  transition-duration: 175ms;
  transition: all 175ms ease-out;

  border-color: ${({ theme, selected }) =>
    selected ? theme.Components.Conversational.ActiveBorder : theme.Components.Conversational.Border};
  background-color: ${({ theme, selected }) =>
    selected ? theme.Components.Conversational.ActiveFill : theme.Components.Conversational.Fill};
  color: ${({ theme, selected }) =>
    selected ? theme.Components.Conversational.ActiveText : theme.Components.Conversational.Text};
`
